/**
 * AdminChromeExtension — Feb 2026 · Super Admin dashboard page.
 *
 * Lets the Super Admin:
 *   - Inspect the currently-shipped extension version + file list.
 *   - Download the source as an "unpacked" zip (developer-mode install).
 *   - Download a Chrome Web Store submission zip (same content — the
 *     store signs & packages on their side).
 *   - See the store listing metadata to copy-paste into the CWS
 *     Developer Dashboard.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Chrome, Download, Package, ShieldCheck, RefreshCw, ExternalLink, Loader2, FileCode, Copy, Check } from "lucide-react";

function formatBytes(b) {
  if (!b) return "0 B";
  const units = ["B", "KB", "MB"];
  let i = 0; let n = b;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

export default function AdminChromeExtension() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [shotUrls, setShotUrls] = useState({});

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch { toast.error("Couldn't copy — press Ctrl/Cmd + C instead"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/chrome-extension/info");
      setInfo(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't load extension info"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Fetch screenshots as blob URLs (need auth headers — <img src> wouldn't send them).
  useEffect(() => {
    const shots = info?.screenshots || [];
    if (!shots.length) return;
    let cancelled = false;
    const created = [];
    (async () => {
      const next = {};
      for (const s of shots) {
        try {
          const resp = await api.get(s.url.replace(/^\/api\/v1/, ""), { responseType: "blob" });
          const u = URL.createObjectURL(resp.data);
          created.push(u);
          next[s.name] = u;
        } catch { /* ignore — tile will show name only */ }
      }
      if (!cancelled) setShotUrls(next);
    })();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [info?.screenshots]);

  const download = async (type) => {
    setDownloading(type);
    try {
      const path = type === "marketing"
        ? "/super-admin/chrome-extension/marketing-bundle"
        : `/super-admin/chrome-extension/download?type=${type}`;
      const resp = await api.get(path, { responseType: "blob" });
      const cd = resp.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const fallback = `acquilix-quick-actions-${info?.version || "1.0.0"}-${type}.zip`;
      const filename = (m && m[1]) || fallback;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Download failed"));
    } finally { setDownloading(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-chrome-extension-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>Chrome Extension</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Download the Acquilix Quick Actions extension source and the Chrome Web Store submission package.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ext-info-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Info card */}
      <div className="border border-border rounded-md p-5" data-testid="ext-info-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-foreground text-background flex items-center justify-center">
            <Chrome className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold">{info?.name || "Acquilix Quick Actions"}</div>
            <div className="text-xs text-muted-foreground">
              Manifest V3 · <span data-testid="ext-info-version">v{info?.version || "—"}</span>
              {info?.file_count != null && <> · {info.file_count} files · {formatBytes(info.total_bytes)}</>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            className="border border-border rounded-md p-4 text-left hover:bg-muted/50 transition-colors disabled:opacity-60"
            onClick={() => download("unpacked")}
            disabled={downloading !== null || loading}
            data-testid="ext-download-unpacked"
          >
            <div className="flex items-center gap-2 mb-1">
              <FileCode className="w-4 h-4" />
              <div className="font-semibold text-sm">Unpacked source .zip</div>
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              For developer install: unzip, then <code>chrome://extensions → Load unpacked</code>.
            </div>
            <div className="text-xs font-medium text-primary inline-flex items-center gap-1">
              {downloading === "unpacked" ? <><Loader2 className="w-3 h-3 animate-spin" /> Downloading…</> : <><Download className="w-3 h-3" /> Download</>}
            </div>
          </button>

          <button
            type="button"
            className="border border-border rounded-md p-4 text-left hover:bg-muted/50 transition-colors disabled:opacity-60"
            onClick={() => download("store")}
            disabled={downloading !== null || loading}
            data-testid="ext-download-store"
          >
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4" />
              <div className="font-semibold text-sm">Chrome Web Store .zip</div>
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              Upload as-is to the Chrome Web Store Developer Dashboard → Package.
            </div>
            <div className="text-xs font-medium text-primary inline-flex items-center gap-1">
              {downloading === "store" ? <><Loader2 className="w-3 h-3 animate-spin" /> Downloading…</> : <><Download className="w-3 h-3" /> Download</>}
            </div>
          </button>
        </div>
      </div>

      {/* Submission checklist */}
      <div className="border border-border rounded-md p-5" data-testid="ext-submission-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4" />
          <div className="font-semibold">Chrome Web Store submission checklist</div>
        </div>
        <ol className="text-sm text-muted-foreground space-y-2 list-decimal pl-5">
          <li>
            Bump <code>manifest.json → version</code> before every submission
            (Chrome enforces strictly-increasing semver).
          </li>
          <li>
            Sign in to the{" "}
            <a href="https://chrome.google.com/webstore/devconsole/" target="_blank" rel="noopener noreferrer"
               className="underline inline-flex items-center gap-1">
              Chrome Web Store Developer Dashboard <ExternalLink className="w-3 h-3" />
            </a>
            {" "}with the Acquilix Google account.
          </li>
          <li>Create (or open) the <strong>Acquilix Quick Actions</strong> item.</li>
          <li>Under <em>Package</em>, upload the <strong>Chrome Web Store .zip</strong> above.</li>
          <li>Fill in Store listing: title, short description, screenshots, privacy policy URL, single purpose description ("Add tasks to Acquilix from anywhere").</li>
          <li>Under <em>Privacy practices</em>, declare host access (only <code>*.acquilix.com</code>) and justify each permission (<code>storage</code>, <code>alarms</code>).</li>
          <li>Submit for review. Google typically responds within a few business days.</li>
        </ol>
      </div>

      {/* Store listing copy — paste-ready fields */}
      {info?.listing?.summary ? (
        <div className="border border-border rounded-md p-5" data-testid="ext-listing-card">
          <div className="font-semibold mb-1">Store listing — paste-ready copy</div>
          <p className="text-xs text-muted-foreground mb-4">
            Copy these fields directly into the Chrome Web Store Developer Dashboard.
            Marketing assets (screenshots, listing copy, privacy policy) can be downloaded
            as a bundle below.
          </p>
          <div className="space-y-4">
            <ListingField
              label="Item name (max 45 chars)"
              value={info.listing.item_name}
              copyKey="item_name"
              copiedKey={copiedKey}
              onCopy={copyToClipboard}
            />
            <ListingField
              label="Summary (max 132 chars)"
              value={info.listing.summary}
              copyKey="summary"
              copiedKey={copiedKey}
              onCopy={copyToClipboard}
            />
            <ListingField
              label="Single-purpose description"
              value={info.listing.single_purpose}
              copyKey="single_purpose"
              copiedKey={copiedKey}
              onCopy={copyToClipboard}
            />
            <ListingField
              label="Detailed description"
              value={info.listing.detailed_description}
              copyKey="detailed_description"
              copiedKey={copiedKey}
              onCopy={copyToClipboard}
              multiline
            />
            <div className="flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">Privacy policy URL</div>
                <a href={info.listing.privacy_url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground underline">
                  {info.listing.privacy_url}
                </a>
              </div>
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(info.listing.privacy_url, "privacy_url")} data-testid="ext-listing-copy-privacy">
                {copiedKey === "privacy_url" ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy URL</>}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Marketing assets — screenshots + bundle download */}
      {info?.screenshots?.length ? (
        <div className="border border-border rounded-md p-5" data-testid="ext-marketing-card">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="font-semibold">Marketing assets</div>
              <p className="text-xs text-muted-foreground mt-1">
                CWS-spec screenshots (1280×800), listing copy and privacy policy — download the
                bundle to upload all at once.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => download("marketing")}
              disabled={downloading !== null}
              data-testid="ext-download-marketing"
            >
              {downloading === "marketing" ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Bundling…</> : <><Download className="w-3 h-3 mr-2" /> Marketing bundle</>}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {info.screenshots.map((s) => (
              <div
                key={s.name}
                className="border border-border rounded-md overflow-hidden hover:shadow-md transition-shadow block cursor-pointer"
                onClick={() => shotUrls[s.name] && window.open(shotUrls[s.name], "_blank")}
                data-testid={`ext-screenshot-${s.name}`}
                title="Click to open full size"
              >
                {shotUrls[s.name] ? (
                  <img src={shotUrls[s.name]} alt={s.name} className="w-full h-auto block bg-muted" loading="lazy" />
                ) : (
                  <div className="w-full aspect-video bg-muted flex items-center justify-center text-xs text-muted-foreground">
                    Loading…
                  </div>
                )}
                <div className="px-3 py-2 text-xs flex items-center justify-between text-muted-foreground bg-muted/30">
                  <span className="truncate font-mono">{s.name}</span>
                  <span>{formatBytes(s.bytes)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* File tree */}
      {info?.files?.length ? (
        <div className="border border-border rounded-md p-5" data-testid="ext-files-card">
          <div className="font-semibold text-sm mb-3">Files included in the zip</div>
          <ul className="text-xs font-mono text-muted-foreground space-y-1 max-h-60 overflow-y-auto">
            {info.files.map((f) => <li key={f}>{f}</li>)}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ListingField({ label, value, copyKey, copiedKey, onCopy, multiline }) {
  const isCopied = copiedKey === copyKey;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium">{label}</div>
        <button
          type="button"
          className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          onClick={() => onCopy(value, copyKey)}
          data-testid={`ext-listing-copy-${copyKey}`}
        >
          {isCopied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      {multiline ? (
        <pre className="text-xs bg-muted/40 border border-border rounded p-3 max-h-60 overflow-y-auto whitespace-pre-wrap font-mono">{value}</pre>
      ) : (
        <div className="text-sm bg-muted/40 border border-border rounded p-2 font-mono">{value}</div>
      )}
    </div>
  );
}
